"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const serviceAreas = [{
  name: "Hudson, WI",
  zip: "54016"
}, {
  name: "River Falls, WI",
  zip: "54022"
}, {
  name: "Prescott, WI",
  zip: "54021"
}, {
  name: "Ellsworth, WI",
  zip: "54011"
}];
export default function WhereWeWork() {
  const {
    ref: cardRef,
    isVisible: isCardVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="where-we-work" className="relative w-full h-[700px] bg-muted overflow-hidden">
      {}
      <div className="absolute inset-0 w-full h-full bg-background">
        <iframe title="Service Area Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d180138.8821451881!2d-92.83880445!3d44.8742685!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87f7d8c8b8b8b8b8%3A0x8b8b8b8b8b8b8b8b!2sHudson%2C%20WI!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus" className="w-full h-full border-0 grayscale sepia-[.15] contrast-125 opacity-60 mix-blend-multiply dark:mix-blend-screen dark:opacity-40 dark:invert" allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        {}
        <div className="absolute inset-0 bg-background/30 pointer-events-none" />
      </div>

      {}
      <div className="container relative h-full mx-auto px-4 md:px-6 flex items-center justify-center md:justify-start">
        <div ref={cardRef} className={`w-full max-w-md transition-all duration-1000 ease-out transform ${isCardVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <Card className="bg-background/95 backdrop-blur-md border-border/50 shadow-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-3xl font-serif text-foreground">
                Areas We Serve
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground mt-2">
                Providing premium remodeling services to homeowners across Western Wisconsin.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pb-6">
              <ul className="space-y-4">
                {serviceAreas.map((area, i) => <li data-index={i} key={area.name} className="flex items-start gap-3">
                    <div className="mt-0.5 bg-primary/10 p-2 rounded-full text-primary">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{area.name}</p>
                      <p className="text-sm text-muted-foreground">Zip: {area.zip}</p>
                    </div>
                  </li>)}
              </ul>

              <div className="mt-8 pt-6 border-t border-border/50 space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">(xxx) xxx-xxxx</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">info@finelydone.com</span>
                </div>
              </div>
            </CardContent>

            <CardFooter className="pt-0">
              <Button asChild className="w-full group">
                <Link to="/ContactPage">
                  Request a Quote
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </section>;
}